
/* eslint-disable local/no-gettext-mismatches */

import { format } from 'JIX/utils';

const translations         = {};
const translations_context = {};
const translations_plural  = {};

export const config = { ignoreMissingTranslations: true };

export function add([msgid, is_plural, msgstr, context]) {
    if (context) {
        translations_context[msgid] || (translations_context[msgid] = {});
        translations_context[msgid][context] = msgstr;
    } else if (is_plural) {
        translations_plural[msgid[0]] || (translations_plural[msgid[0]] = {});
        translations_plural[msgid[0]][msgid[1]] = msgstr;
    } else {
        translations[msgid] = msgstr;
    }
}

export function add_many(objs) {
    objs.forEach((obj) => add(obj));
}

/**
 * Basic gettext translation.
 *
 * @param {String} str The string to translate
 * @returns {String} The translated string
 *
 * @example
 *  __("Dette er min hest")
 */
export function __(str) {
    const trans = translations[str];
    if (!trans) {
        if (!config.ignoreMissingTranslations) {
            console.error("Missing translation for '" + str + "'");
        }
        return str;
    }
    return trans;
}

/**
 * Gettext with singular/plural strings.
 *
 * @param {String} str1 The string in singular form
 * @param {String} str2 The string in plural form
 * @param {Number} n The count of the subject
 * @returns {String} The translated string
 *
 * @example
 * __n("Du har en ny besked", "Du har nye beskeder", messages.length)
 */
export function __n(str1, str2, n) {
    let trans = (translations_plural[str1] || {})[str2];
    if (!trans || !trans[0] || !trans[1]) {
        if (!config.ignoreMissingTranslations) {
            console.error("Missing plural translation for '" + str1 + "', '" + str2 + "'");
        }
        trans = { '0': str1, '1': str2 };
    }

    return trans[n === 1 ? 0 : 1]; // TODO We don't support the Plural-Forms stuff. It doesn't matter for now.
}

/**
 * Gettext with interpolation.
 *
 * @param {String} str The string to translate
 * @param {Object} args The arguments for interpolation
 * @returns {String} The translated string
 *
 * @example
 * __x("Side {page} ud af {num_pages}", { page: current_page, num_pages: num_pages })
 */
export function __x(str, args) {
    const trans = __(str);
    return format(trans, args);
}

/**
 * Combines `__x` and `__n` to provide singular/plural strings with interpolation.
 *
 * @param {String} str1 The string in singular form
 * @param {String} str2 The string in plural form
 * @param {Number} n The count of the subject
 * @param {Object} args The arguments for interpolation
 * @returns {String} The translated string
 *
 * @example
 * __nx("Du har en ny besked", "Du har {count} nye beskeder", message_count, {count: message_count})
 */
export function __nx(str1, str2, n, args) {
    const trans = __n(str1, str2, n);
    return format(trans, args);
}

/**
 * Gettext with context to the translator.
 *
 * @param {String} context The context
 * @param {String} str The string to translate
 * @returns {String} The translated string
 *
 * @example
 * __p("Et landkort", "Kort")
 * __p("Modsat lang", "Kort")
 */
export function __p(context, str) {
    let trans = (translations_context[str] || {})[context];
    if (!trans) {
        if (!config.ignoreMissingTranslations) {
            console.error("Missing translation for '" + str + "' (ctx: '" + context + "')");
        }
        return str;
    }
    return trans;
}

/**
 * A no-op the marks a string to be added to the .po-files.
 * This is useful when using variables for translations.
 * This function is supposed to be used in conjunction with "__N".
 *
 * @param {String} str The string to translate
 * @returns {String} The translated string
 *
 * @example
 * const h = { new: N__("Ny"), closed: N__("Lukket") };
 * console.log(__N(h[key]));
 */
export function N__(str) {
    return str;
}

/**
 * The mirror function of `N__`, and the only gettext function that
 * is allowed to be called with a variable as the text identifier.
 *
 * @param {String} str The string to translate
 * @returns {String} The translated string
 */
export function __N(str) {
    return __(str);
}
